"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_LIMITS_AUTHORING_GUIDE = exports.APP_PLAN_LIMITS = exports.APP_LIMITS = exports.APP_LIMIT_WARN_SHARE = void 0;
exports.appContainerTableKeys = appContainerTableKeys;
exports.appContainerPlanUsage = appContainerPlanUsage;
exports.appBaseConfigPlanUsage = appBaseConfigPlanUsage;
exports.appPlanLimitCount = appPlanLimitCount;
exports.measureAppContainerPlanQuota = measureAppContainerPlanQuota;
exports.appContainerPlanQuotaFinding = appContainerPlanQuotaFinding;
exports.appContainerPlanGrowth = appContainerPlanGrowth;
exports.measureAppContainerLimits = measureAppContainerLimits;
exports.appBindingLimitOf = appBindingLimitOf;
exports.explainAppLimits = explainAppLimits;
exports.appLimit = appLimit;
exports.appLimitLevel = appLimitLevel;
exports.measureAppLimit = measureAppLimit;
exports.renderAppLimitsTable = renderAppLimitsTable;
exports.appLimitsTableIn = appLimitsTableIn;
const database_identity_1 = require("@streamlain/database-identity");
const app_base_bindings_1 = require("./app-base-bindings");
const app_base_contract_1 = require("./app-base-contract");
const budgets_1 = require("./budgets");
/** The tables of an App Container: those of its compiled composition. */
function appContainerTableKeys(input) {
    return (0, app_base_contract_1.compileAppComposition)({
        composition: input.composition,
        appDefinitions: input.appDefinitions,
    }).manifest.dataSourceKeys;
}
/**
 * The one count the plan quota is checked against, in the StreamKit and on the
 * platform alike: saving, publishing, installing and the StreamKit bar.
 */
function appContainerPlanUsage(input) {
    const placed = new Set(app_base_contract_1.AppCompositionSchema.parse(input.composition).mounts.map(mount => mount.appId));
    return {
        tables: appContainerTableKeys(input).length,
        agentActions: input.appDefinitions
            .filter(definition => placed.has(definition.appId))
            .reduce((sum, definition) => sum + (definition.agentActions?.length ?? 0), 0),
    };
}
/** What one App Container config uses of its plan; none without a composition. */
function appBaseConfigPlanUsage(config) {
    if (!config.appComposition)
        return { tables: 0, agentActions: 0 };
    return appContainerPlanUsage({
        composition: config.appComposition,
        appDefinitions: Object.values(config.appDefinitions),
    });
}
/** Share of a limit from which the StreamKit and App Studio warn. */
exports.APP_LIMIT_WARN_SHARE = 0.8;
const SET_NOT_MEASURED = 'Set, not measured; tsk-limits-loadtests measures it and this entry keeps the result.';
exports.APP_LIMITS = [
    {
        id: 'appContainer.tables',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxDataSources,
        unit: 'count',
        scope: 'appContainer',
        purpose: 'Tables one App binds; install, export, trash and restore touch them together.',
        reason: SET_NOT_MEASURED,
        onExceed: 'refuseSave',
        remedy: 'Move App Modules into a second App.',
    },
    {
        id: 'page.tables',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxPageTables,
        unit: 'count',
        scope: 'page',
        purpose: 'Tables one page reads with its frame: permission checks, live subscriptions and one dataset batch.',
        reason: 'User-defined bound; server admission and 32/33-source boundary tests enforce it even for stored manifests that bypassed validation. Workload cost is measured separately.',
        onExceed: 'refuseSave',
        remedy: 'Split the page.',
    },
    {
        id: 'agentAction.tables',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxPageTables,
        unit: 'count',
        scope: 'agentAction',
        purpose: 'Tables one Agent Action binds and checks per call.',
        reason: 'Same centrally defined bound as a page; server admission checks the stored action bindings before database inventory and permission reads.',
        onExceed: 'refuseSave',
        remedy: 'Split the Agent Action.',
    },
    {
        id: 'appContainer.files',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxFiles,
        unit: 'count',
        scope: 'appContainer',
        purpose: 'Files of one App for saving, publishing, installing, import and packages.',
        reason: 'Card dec-streamkit-module-file-limits-1020 = A: one count for every way; each file costs one stored object, one hash and one list entry.',
        onExceed: 'refuseSave',
        remedy: 'Bundle small files or remove unused ones.',
    },
    {
        id: 'appContainer.publishBytes',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxTotalBytes,
        unit: 'bytes',
        scope: 'appContainer',
        purpose: 'Bytes one App publishes: publication memory and CDN cache.',
        reason: 'Files are stored blobs and packages stream them one at a time, so the limit bounds storage and CDN cache, not server memory.',
        onExceed: 'refusePublish',
        remedy: 'Compress assets or remove files; the preview stays available.',
    },
    {
        id: 'appContainer.saveBytes',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxStoredTotalBytes,
        unit: 'bytes',
        scope: 'appContainer',
        purpose: 'Bytes the files of one App Container hold when it is saved.',
        reason: 'The publish limit: every file a save holds can be published, packaged and installed.',
        onExceed: 'refuseSave',
        remedy: 'Compress assets or remove files.',
    },
    {
        id: 'file.bytes',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxFileBytes,
        unit: 'bytes',
        scope: 'file',
        purpose: 'Bytes of one text file (HTML, CSS, JavaScript, JSON).',
        reason: 'Editor, AI tools and preview load a text file whole.',
        onExceed: 'refuseSave',
        remedy: 'Split or compress the file.',
    },
    {
        id: 'file.binaryBytes',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxBinaryFileBytes,
        unit: 'bytes',
        scope: 'file',
        purpose: 'Bytes of one binary file (image, font).',
        reason: 'A binary file travels as a stream into its blob; the bound keeps one App file small beside the App Container.',
        onExceed: 'refuseSave',
        remedy: 'Compress or scale down the image.',
    },
    {
        id: 'appContainer.manifestBytes',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxManifestBytes,
        unit: 'bytes',
        scope: 'appContainer',
        purpose: 'Bytes of the manifest: parsing before anything else runs.',
        reason: 'Room for large command sets while parsing stays bounded.',
        onExceed: 'refuseSave',
        remedy: 'Remove unused commands or routes.',
    },
    {
        id: 'appContainer.navigationItems',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxNavigationItems,
        unit: 'count',
        scope: 'appContainer',
        purpose: 'Navigation entries: validation and the controls built from them.',
        reason: 'Each entry becomes rendered navigation and a control.',
        onExceed: 'refuseSave',
        remedy: 'Group entries under parents.',
    },
    {
        id: 'appContainer.agentActions',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxAgentActions,
        unit: 'count',
        scope: 'appContainer',
        purpose: 'Agent Actions of one App: schemas injected into authoring and runtime registries.',
        reason: 'Twice its largest table count (appContainerAgentActionLimit); one App Module may hold all of them, the plan quota is lower.',
        onExceed: 'refuseSave',
        remedy: 'Merge related Agent Actions.',
    },
    {
        id: 'action.fields',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxActionFields,
        unit: 'count',
        scope: 'agentAction',
        purpose: 'Fields of one command: payload validation and generated controls.',
        reason: 'Each field becomes a generated control.',
        onExceed: 'refuseSave',
        remedy: 'Split the command.',
    },
    {
        id: 'action.payloadBytes',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxActionPayloadBytes,
        unit: 'bytes',
        scope: 'agentAction',
        purpose: 'UTF-8 JSON bytes of one command before validation and execution; includes complete Agent Action arguments from pages, Copilot and MCP.',
        reason: 'Bounds memory before the schema check admits a call.',
        onExceed: 'refuseRender',
        remedy: 'Send large content as an attachment.',
    },
    {
        id: 'read.rows',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxDatasetRows,
        unit: 'rows',
        scope: 'read',
        purpose: 'Rows one request of a host answers at most; a complete read walks requests to the end of the source.',
        reason: 'Bounds the working set and response memory of one request.',
        onExceed: 'warn',
        remedy: 'None needed: further windows follow.',
    },
    {
        id: 'read.windowRows',
        value: app_base_contract_1.APP_BASE_BUDGETS.defaultDatasetRows,
        unit: 'rows',
        scope: 'read',
        purpose: 'Rows of one read window; a full read walks windows of this size.',
        reason: 'One window stays inside the chunk bytes for typical rows.',
        onExceed: 'warn',
        remedy: 'None needed: further windows follow.',
    },
    {
        id: 'read.chunkBytes',
        value: app_base_contract_1.APP_BASE_BUDGETS.maxDatasetChunkBytes,
        unit: 'bytes',
        scope: 'read',
        purpose: 'Bytes of one query response before transport framing.',
        reason: 'Keeps one response inside one frame of the Bridge.',
        onExceed: 'warn',
        remedy: 'None needed: the read continues in the next window.',
    },
    {
        id: 'read.parallel',
        value: app_base_contract_1.APP_BASE_BRIDGE_BUDGETS.parallelReads,
        unit: 'count',
        scope: 'page',
        purpose: 'Reads one page runs at once.',
        reason: 'Bounds the reads one page holds open against the server at once.',
        onExceed: 'warn',
        remedy: 'None needed: further reads wait in the queue.',
    },
    {
        id: 'read.queued',
        value: app_base_contract_1.APP_BASE_BRIDGE_BUDGETS.queuedReads,
        unit: 'count',
        scope: 'page',
        purpose: 'Reads one page queues at once; the further windows of a read that began do not count.',
        reason: 'A page that queues more reads than this loops; it is answered busy.',
        onExceed: 'busy',
        remedy: 'Read fewer tables at once or wait for the reads before.',
    },
    {
        id: 'read.queueWaitMs',
        value: app_base_contract_1.APP_BASE_BRIDGE_BUDGETS.queueWaitMs,
        unit: 'ms',
        scope: 'page',
        purpose: 'Longest wait of a read in the queue while no read ends.',
        reason: 'A queue in which no read ends for this long is stalled; it is answered busy. A slow host that still answers keeps the read waiting.',
        onExceed: 'busy',
        remedy: 'Retry once the server answers again.',
    },
    {
        id: 'binding.nodes',
        value: app_base_bindings_1.APP_BASE_BINDING_BUDGETS.nodes,
        unit: 'count',
        scope: 'page',
        purpose: 'Elements one page renders from its bindings: engine work.',
        reason: `${SET_NOT_MEASURED} Dev Hub /plan needed 21,315 on 19.10.2026.`,
        onExceed: 'refuseRender',
        remedy: 'Split the page; a long list draws only the rows near the viewport.',
    },
    {
        id: 'binding.bytes',
        value: app_base_bindings_1.APP_BASE_BINDING_BUDGETS.bytes,
        unit: 'bytes',
        scope: 'page',
        purpose: 'HTML one page renders from its bindings.',
        reason: 'Bounds server render and the DOM patch of one page.',
        onExceed: 'refuseRender',
        remedy: 'Split the page; a long list draws only the rows near the viewport.',
    },
    {
        id: 'binding.depth',
        value: app_base_bindings_1.APP_BASE_BINDING_BUDGETS.depth,
        unit: 'count',
        scope: 'page',
        purpose: 'Nesting of bound templates.',
        reason: 'Bounds recursion of the binding engine.',
        onExceed: 'refuseRender',
        remedy: 'Flatten nested templates.',
    },
    {
        id: 'binding.readMs',
        value: app_base_bindings_1.APP_BASE_BINDING_BUDGETS.readMs,
        unit: 'ms',
        scope: 'page',
        purpose: 'Longest wait for the data of the bound elements of a page.',
        reason: 'A page shows its error instead of a spinner without end.',
        onExceed: 'refuseRender',
        remedy: 'Read fewer rows on the page.',
    },
    {
        id: 'binding.templateBytes',
        value: app_base_bindings_1.APP_BASE_BINDING_TEMPLATE_MAX_BYTES,
        unit: 'bytes',
        scope: 'page',
        purpose: 'Bytes of one bound HTML template, authoring and render alike.',
        reason: 'Bounds template parsing in the engine.',
        onExceed: 'refuseSave',
        remedy: 'Split the template.',
    },
    {
        id: 'binding.markdownBytes',
        value: app_base_bindings_1.APP_BASE_BINDING_BUDGETS.markdownBytes,
        unit: 'bytes',
        scope: 'cell',
        purpose: 'Markdown of one bound cell before it is rendered.',
        reason: 'Bounds the markdown parser per cell.',
        onExceed: 'refuseRender',
        remedy: 'Shorten the text or split it into rows.',
    },
    {
        id: 'table.rows',
        value: database_identity_1.DATABASE_PRODUCT_LIMITS.rowHardLimit,
        unit: 'rows',
        scope: 'table',
        purpose: 'Rows of one table.',
        reason: 'DATABASE_LARGE_DATA_POLICY: every user and API action keeps a table within it.',
        onExceed: 'refuseSave',
        remedy: 'Archive rows into a second table.',
    },
    {
        id: 'table.columns',
        value: database_identity_1.DATABASE_PRODUCT_LIMITS.columnHardLimit,
        unit: 'count',
        scope: 'table',
        purpose: 'Columns of one table.',
        reason: 'DATABASE_LARGE_DATA_POLICY.',
        onExceed: 'refuseSave',
        remedy: 'Move columns into a related table.',
    },
    {
        id: 'table.rowBytes',
        value: database_identity_1.DATABASE_PRODUCT_LIMITS.rowEncodedByteHardLimit,
        unit: 'bytes',
        scope: 'table',
        purpose: 'Encoded bytes of one row.',
        reason: 'One row always fits into a single row shard.',
        onExceed: 'refuseSave',
        remedy: 'Move large content into attachments.',
    },
    {
        id: 'page.cssBytes',
        value: budgets_1.BUILDER_BUDGETS.appBaseCssOutputHardBlockBytes,
        unit: 'bytes',
        scope: 'appContainer',
        purpose: 'Merged minified CSS of a published App.',
        reason: 'Keeps megabytes of stylesheet out of the CDN pipeline.',
        onExceed: 'refusePublish',
        remedy: 'Remove unused style sheets.',
    },
];
/**
 * Limits the plan of a workspace sets per App Container. Their value is the
 * plan's (public plan API `quotas.appBaseTableLimit` and
 * `appBaseAgentActionLimit`), never a number of this list.
 */
exports.APP_PLAN_LIMITS = [
    {
        id: 'plan.containerTables',
        unit: 'count',
        scope: 'appContainer',
        purpose: 'Tables one App holds on the plan of the workspace.',
        reason: 'Plan quota appBaseTableLimit.',
        onExceed: 'refuseSave',
        remedy: 'Make the new part its own App (another job or audience) or choose a higher plan.',
    },
    {
        id: 'plan.containerAgentActions',
        unit: 'count',
        scope: 'appContainer',
        purpose: 'Agent Actions one App holds on the plan of the workspace.',
        reason: 'Twice the tables of the plan (appContainerAgentActionLimit).',
        onExceed: 'refuseSave',
        remedy: 'Make the new part its own App (another job or audience) or choose a higher plan.',
    },
];
/**
 * The limits as the authoring contract gives them to Copilot and MCP agents
 * (app_dev_authoring_contract), with the rule for 80 percent.
 */
exports.APP_LIMITS_AUTHORING_GUIDE = {
    list: exports.APP_LIMITS,
    planLimits: exports.APP_PLAN_LIMITS,
    warnShare: exports.APP_LIMIT_WARN_SHARE,
    planQuota: 'The plan of the workspace sets the tables of one App (appBaseTableLimit) and its Agent Actions (twice the tables); sl-streamkit check and the StreamKit bar compare with the target plan (--plan).',
    rule: `From ${exports.APP_LIMIT_WARN_SHARE * 100} % of a plan quota or of a limit on, ask the user before adding more: does the new part become its own App (another job or another audience), or does the plan go up? Never split an App Module or an App silently and never drop a requirement to stay below a limit.`,
};
const APP_PLAN_LIMIT_COUNTS = {
    'plan.containerTables': { key: 'tables', words: 'tables' },
    'plan.containerAgentActions': { key: 'agentActions', words: 'Agent Actions' },
};
/** The count of an App Container a plan limit stands for, with its words. */
function appPlanLimitCount(id) {
    const count = APP_PLAN_LIMIT_COUNTS[id];
    if (!count)
        throw new Error(`Unknown plan limit ${id}`);
    return count;
}
/**
 * What an App Container uses of its plan quota, in the form the StreamKit bar,
 * App Studio and app_dev_diagnose show.
 */
function measureAppContainerPlanQuota(usage, quota) {
    return exports.APP_PLAN_LIMITS.map(limit => {
        const { key } = appPlanLimitCount(limit.id);
        return {
            limitId: limit.id,
            used: usage[key],
            limit: quota[key],
            level: appLimitLevel(usage[key], quota[key], exports.APP_LIMIT_WARN_SHARE),
            location: `App on plan ${quota.plan}`,
        };
    });
}
/**
 * The finding of one plan measurement: above the quota the way out, from the
 * warning share on the way out before it grows; null while it is ok.
 */
function appContainerPlanQuotaFinding(usage, plan) {
    if (usage.level === 'ok')
        return null;
    const limit = exports.APP_PLAN_LIMITS.find(entry => entry.id === usage.limitId);
    if (!limit)
        throw new Error(`Unknown plan limit ${usage.limitId}`);
    const text = `App: ${usage.used} ${appPlanLimitCount(limit.id).words} of ${usage.limit} on plan ${plan}`;
    return usage.level === 'over'
        ? `${text}. ${limit.remedy}`
        : `${text}. Before it grows: ${limit.remedy}`;
}
/**
 * The count by which `next` grows above the plan: above it a container may
 * stay as `previous` or shrink, never grow. Null within the plan or without
 * growth. Saving, publishing and installing refuse it; App Studio blocks
 * publishing on it.
 */
function appContainerPlanGrowth(next, previous, quota) {
    for (const limit of exports.APP_PLAN_LIMITS) {
        const { key } = appPlanLimitCount(limit.id);
        if (next[key] > quota[key] && next[key] > previous[key])
            return key;
    }
    return null;
}
const APP_CONTAINER_LOCATION = 'App';
/**
 * What an App Container uses of every limit it is measured against before it
 * runs: the usages the StreamKit bar and App Studio show alike. The bar adds
 * what a page renders (`binding.*`), which only the running page knows.
 */
function measureAppContainerLimits(input) {
    const container = (0, app_base_contract_1.appArtifactUsage)({ artifact: input.artifact });
    return [
        measureAppLimit('appContainer.tables', container.dataSources.used, APP_CONTAINER_LOCATION),
        measureAppLimit('appContainer.files', container.files.used, APP_CONTAINER_LOCATION),
        measureAppLimit('appContainer.publishBytes', container.bytes.used, APP_CONTAINER_LOCATION),
        measureAppLimit('appContainer.saveBytes', container.bytes.used, APP_CONTAINER_LOCATION),
        ...input.routes.map(route => measureAppLimit('page.tables', new Set(route.dataSourceKeys).size, route.path)),
        ...input.agentActions.map(action => measureAppLimit('agentAction.tables', action.bindings.length, `Agent Action ${action.id}`)),
        measureAppLimit('appContainer.agentActions', input.containerAgentActions, APP_CONTAINER_LOCATION),
    ];
}
/**
 * The listed limit a render or read failure of the binding engine names
 * (`(limit binding.nodes)`), so the preview shows it like the StreamKit bar;
 * null for any other failure.
 */
function appBindingLimitOf(message) {
    const id = /\(limit (binding\.[A-Za-z]+)\)/.exec(message)?.[1];
    return exports.APP_LIMITS.find(limit => limit.id === id) ?? null;
}
/**
 * A refusal names the limit it ran into as `(limit <id>)`; every place that
 * hands a refusal to a person adds what the limit protects and the way out,
 * from this list.
 */
function explainAppLimits(message) {
    return message.replace(/\(limit ([A-Za-z]+\.[A-Za-z]+)\)/g, (token, id) => {
        const limit = exports.APP_LIMITS.find(entry => entry.id === id);
        return limit ? `(limit ${id}: ${limit.purpose} ${limit.remedy})` : token;
    });
}
function appLimit(id) {
    const found = exports.APP_LIMITS.find(limit => limit.id === id);
    if (!found)
        throw new Error(`Unknown App limit ${id}`);
    return found;
}
/**
 * Level of `used` against `limit`: a warning from `warnShare` of it on.
 * Self-contained: the StreamKit bar embeds it as source text.
 */
function appLimitLevel(used, limit, warnShare) {
    if (used > limit)
        return 'over';
    // Nothing used of nothing granted is no warning.
    return used > 0 && used >= limit * warnShare ? 'warn' : 'ok';
}
/** One measurement against a listed limit. */
function measureAppLimit(id, used, location) {
    const { value } = appLimit(id);
    return {
        limitId: id,
        used,
        limit: value,
        level: appLimitLevel(used, value, exports.APP_LIMIT_WARN_SHARE),
        ...(location ? { location } : {}),
    };
}
const AUTO_START = '<!-- app-limits:start (generated by renderAppLimitsTable) -->';
const AUTO_END = '<!-- app-limits:end -->';
/** The table of every limit, as docs/app-base-binding-contract.md carries it. */
function renderAppLimitsTable() {
    const rows = exports.APP_LIMITS.map(limit => `| \`${limit.id}\` | ${limit.value} ${limit.unit} | ${limit.purpose} | ${limit.reason} | ${limit.onExceed} | ${limit.remedy} |`);
    return [
        AUTO_START,
        '<!-- prettier-ignore-start -->',
        '| Limit | Value | Purpose | Reason | Above it | Remedy |',
        '|---|---|---|---|---|---|',
        ...rows,
        '<!-- prettier-ignore-end -->',
        AUTO_END,
    ].join('\n');
}
/** The generated section of a document, or null without the markers. */
function appLimitsTableIn(document) {
    const start = document.indexOf(AUTO_START);
    const end = document.indexOf(AUTO_END);
    if (start < 0 || end < start)
        return null;
    return document.slice(start, end + AUTO_END.length);
}
